import { cloneDeep, isFinite } from 'lodash';

/**
 * Prepares data for waterfall chart, and also returns min and max profit_absolute values for the transformed data.
 * @param data - Array of items, each with profit_absolute.
 * @returns { chartData, min, max }
 */
export const getWaterFallChartData = (
    data: any[],
    previousYearData: number,
    currentYearProfitData: number,
    selectedGrowthMarginDriverKey: string,
) => {
    if (!data || !data.length) return { chartData: [], min: 0, max: 0 };

    const growthDriversData = cloneDeep(data);

    // Calculate total length (sum of absolute values for proper start/end points)
    const totalLength = growthDriversData.reduce((acc, curr) => {
        let diff = 0;
        if (Array.isArray(curr[selectedGrowthMarginDriverKey])) {
            diff = curr[selectedGrowthMarginDriverKey][1] - curr[selectedGrowthMarginDriverKey][0];
        } else {
            diff = curr[selectedGrowthMarginDriverKey];
        }
        return acc + diff;
    }, 0);

    // Start from previousYearData instead of 0
    let prevEnd = previousYearData || 0;
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;

    for (let i = 0; i < growthDriversData.length; i++) {
        const item = growthDriversData[i];

        // Calculate value for this bar
        let value = 0;
        if (Array.isArray(item[selectedGrowthMarginDriverKey])) {
            value = item[selectedGrowthMarginDriverKey][1] - item[selectedGrowthMarginDriverKey][0];
        } else {
            value = item[selectedGrowthMarginDriverKey];
        }

        // First item - starts from previousYearData
        if (i === 0) {
            // Set as array: [previousYearData, previousYearData + value]
            item[selectedGrowthMarginDriverKey] = [prevEnd, prevEnd + value];
            prevEnd = prevEnd + value;
        } else if (i === growthDriversData.length - 1) {
            // Last item - should cover from prevEnd up to previousYearData + totalLength
            const finalEnd = (previousYearData || 0) + totalLength;
            item[selectedGrowthMarginDriverKey] = [prevEnd, finalEnd];
            prevEnd = finalEnd;
        } else {
            // Middle items
            item[selectedGrowthMarginDriverKey] = [prevEnd, prevEnd + value];
            prevEnd += value;
        }

        // Compute min and max for each transformed bar
        const [barStart, barEnd] = item[selectedGrowthMarginDriverKey];
        min = Math.min(min, barStart, barEnd);
        max = Math.max(max, barStart, barEnd);

        // Handle trend for both positive and negative values
        let diff = barEnd - barStart;
        item.trend = diff < 0 ? 'negative' : 'positive';
    }

    // fallback in case of nothing
    min = isFinite(min) ? min : 0;
    max = isFinite(max) ? max : 0;

    min = Math.min(min, previousYearData, currentYearProfitData);
    max = Math.max(max, previousYearData, currentYearProfitData);

    return { chartData: growthDriversData, min, max };
};
